import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    Fragment,
} from "react";
import Loader from "src/components/loader/Loader";

const RegionConfigContext = createContext(null);

const STATIC_BASE = "/static/";

const ASSET_FOLDERS = {
    logo: "logos",
    socials: "socials",
    brands: "brands",
    sidebarBanner: "sidebar_banners",
    icons: "icons",
};

const PATH_MAP = {
    logo: { folder: ASSET_FOLDERS.logo, field: "src" },
    socials: { folder: ASSET_FOLDERS.socials, field: "icon" },
    brands: { folder: ASSET_FOLDERS.brands, field: "icon" },
    sidebarBanner: { folder: ASSET_FOLDERS.sidebarBanner, field: "src" },
};

const prefixSrc = (src, folder) =>
    src
        ? src.startsWith("http://") ||
          src.startsWith("https://") ||
          src.startsWith("data:") ||
          src.startsWith(`${STATIC_BASE}`)
            ? src
            : `${STATIC_BASE}${folder}/${src}`
        : null;

const resolveAssetFolder = (name) => {
    if (!name) {
        return null;
    }
    return ASSET_FOLDERS[name] || name;
};

const mergeConfig = (defaults, overrides) => {
    if (Array.isArray(overrides)) {
        return overrides;
    }
    if (
        defaults &&
        typeof defaults === "object" &&
        overrides &&
        typeof overrides === "object"
    ) {
        const result = { ...defaults };
        Object.keys(overrides).forEach((key) => {
            result[key] = mergeConfig(defaults[key], overrides[key]);
        });
        return result;
    }
    return overrides !== undefined ? overrides : defaults;
};

const enrichLandingItems = (items, inheritedFolder = null) => {
    if (!Array.isArray(items)) {
        return [];
    }
    return items.map((item) => {
        if (!item || typeof item !== "object") {
            return item;
        }
        const itemFolder = resolveAssetFolder(item.assetFolder) || inheritedFolder;
        const enrichedItem = { ...item };

        if (typeof enrichedItem.icon === "string" && itemFolder) {
            enrichedItem.icon = prefixSrc(enrichedItem.icon, itemFolder);
        }
        if (typeof enrichedItem.src === "string" && itemFolder) {
            enrichedItem.src = prefixSrc(enrichedItem.src, itemFolder);
        }
        if (Array.isArray(enrichedItem.items)) {
            enrichedItem.items = enrichLandingItems(enrichedItem.items, itemFolder);
        }
        return enrichedItem;
    });
};

const enrichAssets = (config) => {
    const enriched = { ...config };

    Object.entries(PATH_MAP).forEach(([key, { folder, field }]) => {
        const item = config[key];

        if (Array.isArray(item)) {
            enriched[key] = item.map((entry) => ({
                ...entry,
                [field]: prefixSrc(entry[field], folder),
            }));
        } else if (typeof item === "object" && item !== null) {
            enriched[key] = {
                ...item,
                [field]: prefixSrc(item[field], folder),
            };
        }
    });

    if (
        enriched.landing &&
        Array.isArray(enriched.landing.sections)
    ) {
        enriched.landing = {
            ...enriched.landing,
            sections: enriched.landing.sections.map((section) => {
                const sectionFolder = resolveAssetFolder(section.assetFolder);
                return {
                    ...section,
                    items: enrichLandingItems(section.items, sectionFolder),
                };
            }),
        };
    }

    return enriched;
};

export const RegionConfigProvider = ({ children }) => {
    const region = process.env.REACT_APP_LANG || "de";
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${STATIC_BASE}config.json`)
            .then((res) => res.json())
            .then((data) => {
                const defaultConfig = data["default"] || {};
                const regionOverrides = data[region] || {};
                const resolvedConfig = mergeConfig(defaultConfig, regionOverrides);
                setConfig(enrichAssets(resolvedConfig));
            })
            .catch((err) => {
                console.error("Error while loading config.json:", err);
                setConfig({});
            })
            .finally(() => setLoading(false));
    }, [region]);

    return (
        <Fragment>
            {loading ? (
                <Loader />
            ) : (
                <RegionConfigContext.Provider value={config}>
                    {children}
                </RegionConfigContext.Provider>
            )}
        </Fragment>
    );
};

export const useRegionConfig = () => {
    const context = useContext(RegionConfigContext);
    if (!context) {
        console.warn("useRegionConfig must be used within RegionConfigProvider");
    }
    return context || {};
};
